import { useState, useEffect, useRef, useCallback } from 'react'

import { Box } from '@mui/material'
import GoogleMapReact from 'google-map-react'

import {
  UnverifiedAddressInfo,
  DriverIcon,
  DeliveryCompletedIcon,
} from 'components/bookings/MapRoute'
import { ALPHABET, API_KEY, BRISTOL_COORDINATES } from 'constants/TrackingMap'
import {
  BookingAddressType,
  DriverLocation,
  GetBookingByTrackingHashQuery,
} from 'generated/graphql'

import { isDriverTrackingAllowed } from '../helpers'

interface IOrderableAddressListItem {
  id: string
  name: React.ReactNode
  description: React.ReactNode
  letter: string
  placeId: string | null | undefined
  isVerified: boolean
}

type BookingDetails = NonNullable<
  NonNullable<GetBookingByTrackingHashQuery['bookingByTrackingHash']>['booking']
>

interface IProps {
  bookingDetails: BookingDetails
  driverLocations: Array<DriverLocation>
  hideRoute?: boolean
  referenceAddressId?: any
}

export const BookingTrackingMap = (props: IProps) => {
  const { bookingDetails, driverLocations, hideRoute, referenceAddressId } = props
  const [addressItems, setAddressItems] = useState<IOrderableAddressListItem[]>([])
  const [isMapEnabled, setIsMapEnabled] = useState(true)
  const [isMapReady, setIsMapReady] = useState(false)
  const [deliveryPins, setDeliveryPins] = useState<Array<{ lat: number; lng: number; id: string }>>([])
  const mapRef = useRef<any>(null)
  const mapsApiRef = useRef<any>(null)
  const directionsRendererRef = useRef<any>(null)
  const referenceMarkerRef = useRef<any>(null)
  const isUserInteractingRef = useRef(false)
  const driverLocationsRef = useRef(driverLocations)

  const bookingAddresses = bookingDetails?.addresses
  const referenceAddress = referenceAddressId
    ? (bookingDetails?.addresses ?? []).find((a) => String(a.id) === String(referenceAddressId))
    : null
  const isReferenceAddressSigned = !!(referenceAddress as any)?.signedAt

  useEffect(() => {
    driverLocationsRef.current = driverLocations
  }, [driverLocations])

  useEffect(() => {
    if (
      !isMapReady ||
      isUserInteractingRef.current ||
      !isDriverTrackingAllowed(bookingDetails?.status)
    ) {
      return
    }

    const validDriverLocation = driverLocations?.find(
      (location) =>
        location?.latitude &&
        location?.longitude &&
        !Number.isNaN(Number(location.latitude)) &&
        !Number.isNaN(Number(location.longitude)),
    )

    if (!validDriverLocation) return

    mapRef.current.panTo({
      lat: Number(validDriverLocation.latitude),
      lng: Number(validDriverLocation.longitude),
    })
  }, [driverLocations, isMapReady, bookingDetails?.status])

  useEffect(() => {
    if (!bookingAddresses) {
      return
    }
    if (bookingAddresses.some((x) => !x?.address.isVerified)) {
      setIsMapEnabled(false)
    }

    const defaultItems: IOrderableAddressListItem[] =
      bookingAddresses
        .slice()
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        .map((bookingAddress, index) => {
          const name =
            bookingAddress.type === BookingAddressType.Collection ? 'Collection' : 'Delivery'

          return {
            id: bookingAddress.id,
            name,
            description: '',
            letter: ALPHABET[index] ?? 'n/a',
            placeId: bookingAddress.address.googleMapsPlaceId,
            isVerified: bookingAddress.address.isVerified,
          }
        }) || []

    setAddressItems(defaultItems)
  }, [bookingAddresses, isMapEnabled])

  useEffect(() => {
    if (!isMapReady || !hideRoute || !referenceAddressId || !mapRef.current || !mapsApiRef.current) return

    const fitToVisible = (anchorPosition: { lat: number; lng: number }) => {
      const allPositions: { lat: number; lng: number }[] = [anchorPosition]

      ;(driverLocationsRef.current ?? [])
        .filter(
          (loc) =>
            loc?.latitude &&
            loc?.longitude &&
            !Number.isNaN(Number(loc.latitude)) &&
            !Number.isNaN(Number(loc.longitude)),
        )
        .forEach((loc) =>
          allPositions.push({ lat: Number(loc.latitude), lng: Number(loc.longitude) }),
        )

      if (allPositions.length === 1) {
        mapRef.current.panTo(anchorPosition)
        mapRef.current.setZoom(16)
      } else {
        const bounds = new mapsApiRef.current.LatLngBounds()
        allPositions.forEach((pos) => bounds.extend(pos))
        mapRef.current.fitBounds(bounds, 80)
      }
    }

    const placeNativeMarker = (position: { lat: number; lng: number }) => {
      if (referenceMarkerRef.current) {
        referenceMarkerRef.current.setMap(null)
      }
      referenceMarkerRef.current = new mapsApiRef.current.Marker({
        position,
        map: mapRef.current,
      })
      if (!isUserInteractingRef.current) {
        fitToVisible(position)
      }
    }

    const geom = bookingDetails?.deliveryGeometryLocation
    if (geom?.latitude && geom?.longitude) {
      placeNativeMarker({ lat: geom.latitude, lng: geom.longitude })
      return
    }

    const refItem = addressItems.find((a) => String(a.id) === String(referenceAddressId))
    if (!refItem?.placeId) return

    const geocoder = new mapsApiRef.current.Geocoder()
    geocoder.geocode({ placeId: refItem.placeId }, (results: any[], status: string) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location
        placeNativeMarker({ lat: loc.lat(), lng: loc.lng() })
      }
    })
  }, [isMapReady, hideRoute, referenceAddressId, bookingDetails?.deliveryGeometryLocation, addressItems])

  useEffect(() => {
    return () => {
      if (referenceMarkerRef.current) {
        referenceMarkerRef.current.setMap(null)
      }
    }
  }, [])

  useEffect(() => {
    if (!isMapReady || !mapsApiRef.current || hideRoute) return
    const signedDeliveries = (bookingDetails?.addresses ?? []).filter(
      (a) =>
        a.type === BookingAddressType.Delivery &&
        (a as any).signedAt &&
        a.address.googleMapsPlaceId,
    )
    if (signedDeliveries.length === 0) return
    const geocoder = new mapsApiRef.current.Geocoder()
    signedDeliveries.forEach((addr) => {
      geocoder.geocode(
        { placeId: addr.address.googleMapsPlaceId },
        (results: any[], status: string) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location
            const id = String(addr.id)
            const coords = { lat: loc.lat(), lng: loc.lng(), id }
            setDeliveryPins((prev) =>
              prev.some((p) => p.id === id) ? prev : [...prev, coords],
            )
          }
        },
      )
    })
  }, [isMapReady, hideRoute, bookingDetails?.addresses])

  const handleApiLoaded = useCallback(
    (map, maps) => {
      mapRef.current = map
      mapsApiRef.current = maps
      setIsMapReady(true)

      if (addressItems.length < 2 || hideRoute) return

      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null)
      }

      const directionsRenderer = new maps.DirectionsRenderer()
      const directionsService = new maps.DirectionsService()
      directionsRendererRef.current = directionsRenderer
      directionsRenderer.setMap(map)

      let origin: { placeId: string | null | undefined } = { placeId: '' }
      let destination: { placeId: string | null | undefined } = { placeId: '' }
      const waypoints: Array<typeof maps.DirectionsWaypoint> = []

      addressItems.forEach((address, index) => {
        if (index === 0) {
          origin = { placeId: address.placeId }
        } else if (index === addressItems.length - 1) {
          destination = { placeId: address.placeId }
        } else {
          waypoints.push({
            location: { placeId: address.placeId },
            stopover: true,
          })
        }
      })

      const request = {
        origin,
        destination,
        waypoints,
        provideRouteAlternatives: true,
        travelMode: 'DRIVING',
        drivingOptions: {
          departureTime: new Date(),
        },
        unitSystem: maps.UnitSystem.IMPERIAL,
      }

      directionsService.route(request, (response, status) => {
        if (status === 'OK') {
          directionsRenderer.setDirections(response)
        }
      })
    },
    [addressItems, hideRoute],
  )

  const onMapInteraction = () => {
    isUserInteractingRef.current = true
  }

  const getDriverPinsLocations = () => {
    if (!isDriverTrackingAllowed(bookingDetails?.status) || isReferenceAddressSigned) {
      return null
    }

    const validDriverLocations = driverLocations.filter(
      (location) =>
        location?.latitude &&
        location?.longitude &&
        !Number.isNaN(Number(location.latitude)) &&
        !Number.isNaN(Number(location.longitude)),
    )

    return validDriverLocations.map((location) => (
      <DriverIcon
        key={location.driverId}
        lat={Number(location.latitude)}
        lng={Number(location.longitude)}
      />
    ))
  }

  const getDeliveryPins = () => {
    return deliveryPins.map((pin) => (
      <DeliveryCompletedIcon key={pin.id} lat={pin.lat} lng={pin.lng} />
    ))
  }

  return (
    <>
      {isMapEnabled ? (
        <Box height='100%' width='100%'>
          <GoogleMapReact
            bootstrapURLKeys={{ key: API_KEY }}
            defaultCenter={BRISTOL_COORDINATES}
            defaultZoom={11}
            onDrag={onMapInteraction}
            onDragEnd={onMapInteraction}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}>
            {getDriverPinsLocations()}
            {getDeliveryPins()}
          </GoogleMapReact>
        </Box>
      ) : (
        <UnverifiedAddressInfo />
      )}
    </>
  )
}
